import { createFileRoute } from '@tanstack/react-router'
import { FileText } from 'lucide-react'

import { useCurrencyFormatter } from '@/hooks'
import { usePayslips, type PayslipStatus, type PayslipWithEmployee } from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/hr/salaries/my-payslips')({
  component: MyPayslipsPage,
})

const STATUS_LABELS: Record<PayslipStatus, string> = {
  draft: '草稿',
  pending: '待审批',
  approved: '已审批',
  paid: '已发放',
}

const STATUS_COLORS: Record<PayslipStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
}



function MyPayslipsPage() {
  const { data, isLoading, error } = usePayslips({ pageSize: 50 })
  const { formatMoney } = useCurrencyFormatter()

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const payslips = data?.data ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">我的薪资单</h1>
        <p className="text-muted-foreground">查看个人薪资单明细</p>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : payslips.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <FileText className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无薪资单</p>
            <p className="text-sm text-muted-foreground">薪资单将在每月生成后显示</p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {payslips.map((payslip: PayslipWithEmployee) => (
            <div key={payslip.id} className="rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold">{payslip.month}</span>
                <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[payslip.status])}>
                  {STATUS_LABELS[payslip.status]}
                </span>
              </div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">基本工资</dt>
                  <dd>{formatMoney(payslip.baseSalary)}</dd>
                </div>
                {payslip.allowance && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">津贴</dt>
                    <dd>{formatMoney(payslip.allowance)}</dd>
                  </div>
                )}
                {payslip.deduction && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">扣款</dt>
                    <dd className="text-destructive">-{formatMoney(payslip.deduction)}</dd>
                  </div>
                )}
                {payslip.unpaidLeaveDays && payslip.unpaidLeaveDays > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">事假天数</dt>
                    <dd>{payslip.unpaidLeaveDays} 天</dd>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 font-medium">
                  <dt>实发工资</dt>
                  <dd className="text-primary">{formatMoney(payslip.netSalary)}</dd>
                </div>
              </dl>
              {payslip.paidAt && (
                <p className="mt-3 text-xs text-muted-foreground">
                  发放时间: {new Date(payslip.paidAt).toLocaleString('zh-CN')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
